import Dialog from "@material-ui/core/Dialog";
import Paper from '@material-ui/core/Paper';
import makeStyles from '@material-ui/core/styles/makeStyles';
import withStyles from "@material-ui/core/styles/withStyles";
import {WindowBorder} from "./WindowBorder";
import React, {PropsWithChildren, ReactNode, useCallback, useMemo, useState} from "react";
import {ModalKey} from 'module/Modal/type/ModalKey';
import _ from 'lodash';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import {Theme} from '@material-ui/core';
import {ModalPosition, ModalSize} from 'module/Modal/reducer/ModalType';

const ModalTitlePaper = withStyles(theme => ({
	root: {
		backgroundColor: _.get(theme, ['overrides', 'MuiDialog', 'paper', 'backgroundColor']),
	},
}), {name: 'ModalTitlePaper'})(Paper)

const PaperFitted = withStyles({
	root: {
		margin: '0 !important',
		width: '100%',
		height: '100%',
		maxWidth: 'none !important',
		maxHeight: 'none !important',
	},
}, {name: 'ModalPaperFitted'})(Paper)

const useStyles = makeStyles<Theme, { padContent: boolean }>(theme => ({
	scrollContent: {
		width: '100%',
		height: '100%',
		overflow: 'auto',
		background: 'inherit',
		boxSizing: 'border-box',
		padding: ({padContent}) => padContent ? theme.spacing(1, 3) : '',
	},
	title: {
		display: 'flex',
		justifyContent: 'space-between',
		flexWrap: 'wrap-reverse',
		alignItems: 'center',
		cursor: 'default',
		padding: theme.spacing(0.5, 3),
		fontSize: '1.2rem',
		borderRadius: 0,
		border: '1px solid #d2d1d1',
	},
	titleText: {
		flexGrow: 1,
	},
	titleCloseButton: {
		flexGrow: 0,
		padding: theme.spacing(1),
	},
	padContent: {
		padding: theme.spacing(1, 3),
		borderSizing: 'border-box',
	},
}), {name: 'ModalComponent'})

const PopupBodyComponent = React.memo(({
	propsWindowBorder,
	title,
	padContent,
	onClose,
	children,
	...props
}: any) => {
	const classes = useStyles({padContent})
	return (
		<WindowBorder {...propsWindowBorder}>
			<PaperFitted {...props}>
				{title &&
				<ModalTitlePaper>
					<div className={classes.title}>
						<div className={classes.titleText}>
							{title}
						</div>
						<IconButton className={classes.titleCloseButton} onClick={onClose}><CloseIcon/></IconButton>
					</div>
				</ModalTitlePaper>
				|| null
				}
				<div className={classes.scrollContent}>
					{children}
				</div>
			</PaperFitted>
		</WindowBorder>
	)
})

export interface ModalComponentProps extends PropsWithChildren<{}> {
	modalKey?: ModalKey
	open: boolean
	title?: string | ReactNode
	defaultPosition?: { x?: number, y?: number }
	defaultSize?: { height?: number, width?: number }
	minWidth?: number
	minHeight?: number
	keepMounted?: boolean
	disableAutoFocus?: boolean
	closeOnBackdropClick?: boolean
	/**
	 * use on dialogs with dropdown
	 */
	doubleEscClose?: boolean
	onClose: React.ReactEventHandler
	onChangedPositionSize?: (position?: ModalPosition, size?: ModalSize) => void
	padContent?: boolean
}

export const ModalComponent = React.memo(({
	modalKey,
	open = false,
	title,
	defaultPosition: propsDefaultPosition = {},
	defaultSize: propsDefaultSize = {},
	minWidth = 50,
	minHeight = 50,
	keepMounted,
	disableAutoFocus,
	closeOnBackdropClick = true,
	doubleEscClose = false,
	padContent,
	onClose: externalOnClose,
	onChangedPositionSize,
	children,
	...otherProps
}: ModalComponentProps) => {
	let defaultSize = {
		width: propsDefaultSize.width || Math.max(Math.floor(window.innerWidth * 0.6), 500),
		height: propsDefaultSize.height || Math.max(Math.floor(window.innerHeight * 0.8), 600),
	}
	let defaultPosition = {
		x: propsDefaultPosition.x === 0 ? 0 : propsDefaultPosition.x || Math.max((window.innerWidth - defaultSize.width) / 2, 0),
		y: propsDefaultPosition.y === 0 ? 0 : propsDefaultPosition.y || Math.max((window.innerHeight - defaultSize.height) / 2, 0),
	}
	const propsWindowBorder = useMemo(() => ({
		modalKey,
		minWidth,
		minHeight,
		defaultSize,
		defaultPosition,
		onChangedPositionSize,
	}), [modalKey, minWidth, minHeight, defaultSize, defaultPosition, onChangedPositionSize]);

	const [escCount, setEscCount] = useState(0)

	const onClose = useCallback((evt: any, reason?: 'escapeKeyDown' | 'backdropClick') => {
		switch (reason) {
			case 'backdropClick':
				if (closeOnBackdropClick) externalOnClose(evt)
				break;
			case 'escapeKeyDown':
				if (!doubleEscClose) {
					externalOnClose(evt)
				} else {
					if (escCount > 0) {
						externalOnClose(evt)
					} else {
						setEscCount(escCount + 1);
						setTimeout(() => setEscCount(0), 400)
					}
				}
				break;
			default:
				externalOnClose(evt)
		}
	}, [externalOnClose, escCount, doubleEscClose, closeOnBackdropClick])

	return (
		<Dialog
			{...otherProps}
			open={open}
			keepMounted={keepMounted}
			disableAutoFocus={disableAutoFocus}
			onClose={onClose}
			PaperComponent={PopupBodyComponent}
			PaperProps={{propsWindowBorder, title, onClose, padContent} as any}
		>
			{children}
		</Dialog>
	)
})
